import React, { useState } from 'react';
import { Modal, Button } from 'antd';
import FeatherIcon from 'feather-icons-react';
import styled from 'styled-components';
import { formatImagePath } from 'utility/utility';
import { UnitAttachmentDto } from 'api/models';

const NavigationButton = styled(Button)`
  position: absolute;
  color: #666;
  height: 60px;
  width: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
  border: none;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  z-index: 1;
`;

const BackgroundBlur = styled.div<{ image: string }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url(${props => props.image}) center/cover no-repeat;
  filter: blur(40px);
  opacity: 0.2;
  z-index: -1;
`;

const ImageContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
`;

const ThumbnailsContainer = styled.div`
  height: 100px;
  background: #fff;
  border-top: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px;
  overflow-x: auto;
`;

const ThumbnailWrapper = styled.div<{ isSelected: boolean }>`
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  cursor: pointer;
  border: 2px solid ${props => props.isSelected ? '#1890ff' : 'transparent'};
  opacity: ${props => props.isSelected ? 1 : 0.6};
  transition: all 0.3s;
`;

interface ImageGalleryModalProps {
  visible: boolean;
  onClose: () => void;
  attachments: UnitAttachmentDto[];
}

export const ImageGalleryModal: React.FC<ImageGalleryModalProps> = ({ visible, onClose, attachments }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const images = attachments.filter(att => att.fileType?.startsWith('image/'));
  
  const getCurrentImage = () => images[selectedIndex]?.filePath || '';

  return (
    <Modal
      visible={visible}
      footer={null}
      onCancel={onClose}
      width="90%"
      style={{ maxWidth: '1200px' }}
      bodyStyle={{
        padding: 0,
        borderRadius: '8px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
      centered
    >
      <BackgroundBlur image={formatImagePath(getCurrentImage())} />
      
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', height: '70vh' }}>
        <NavigationButton
          style={{ left: 20 }}
          type="text"
          icon={<FeatherIcon icon="chevron-left" size={36} />}
          onClick={() => setSelectedIndex(prev => (prev > 0 ? prev - 1 : images.length - 1))}
        />

        <ImageContainer>
          <img
            src={formatImagePath(getCurrentImage())}
            alt={images[selectedIndex]?.fileName || ''}
            style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
          />
        </ImageContainer>

        <NavigationButton
          style={{ right: 20 }}
          type="text"
          icon={<FeatherIcon icon="chevron-right" size={36} />}
          onClick={() => setSelectedIndex(prev => (prev < images.length - 1 ? prev + 1 : 0))}
        />
      </div>

      <ThumbnailsContainer>
        {images.map((image, index) => (
          <ThumbnailWrapper
            key={image.id}
            isSelected={index === selectedIndex}
            onClick={() => setSelectedIndex(index)}
          >
            <img
              src={formatImagePath(image.filePath)}
              alt={image.fileName || ''}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </ThumbnailWrapper>
        ))}
      </ThumbnailsContainer>
    </Modal>
  );
}; 